import gsap, { Linear } from 'gsap';
import Stopwatch from './Stopwatch';
import { WATERMARK } from '../media/MediaTypes';
import AlignmentGuides from './Draggable/AlignmentGuides';
import logger from './logger';

gsap.defaultEase = Linear.easeNone;

function isWatermark(item) {
  return item.id === WATERMARK;
}

function activeForTimestamp(item, timestamp) {
  return item.start <= timestamp && timestamp < item.end;
}

function endsAfterItem(itemA, itemB) {
  return itemA.end > itemB.end;
}

function calculateDuration(mediaList = []) {
  // watermarks don't count when calculating project duration
  return Math.max(
    0,
    ...mediaList.filter((item) => !isWatermark(item)).map((item) => item.end)
  );
}

/**
 * ProjectTimeline keeps track of which media items are currently active and inactive
 */
export default class ProjectTimeline {
  duration = 0;

  _requestedTime = 0;

  renderedTime = 0;

  media = {
    all: [],
    past: [], // order here doesn't matter
    active: [], // asc sort by end
    future: [], // asc sort by start
  };

  stopwatch = new Stopwatch();

  greensockTimeline = gsap.timeline();

  /** @type {PIXI.Container} */
  _pixiStage = null;

  _seekingTo = null;

  get seeking() {
    return !!this._seekingTo;
  }

  get readyForSync() {
    return !this.seeking && this.stopwatch.currentTime !== this._requestedTime;
  }

  loadProject(mediaList, app, timestamp = this._requestedTime) {
    this.app = app;
    this._pixiStage = app.stage;
    this._initMedia(mediaList);
    this.duration = calculateDuration(mediaList);
    this._buildGreensockTimeline();
    return this.synchronizeMediaAndRender(timestamp, true);
  }

  clear() {
    return this.loadProject([], { stage: null }, 0);
  }

  pause() {
    this.greensockTimeline.pause();
    this.media.active.map((item) => item.pause());
    this.stopwatch.pause();
  }

  play() {
    this.media.active.map((item) => item.play());
    this.greensockTimeline.play();
    this.stopwatch.play();
  }

  /**
   * Render media for a given timestamp
   *
   * By default, don't seek media items when renderer is playing
   */
  async synchronizeMediaAndRender(
    timestamp = this.stopwatch.currentTime,
    seek = false
  ) {
    if (this.seeking && seek) {
      logger.warn('Requested project seek before previous one completed.', {
        requested: timestamp,
        previous: this._seekingTo,
      });
    }
    if (seek) {
      this._seekingTo = timestamp;
    }
    // track which items should be active for this timestamp
    this._updateMediaTimeline(timestamp);

    if (seek) {
      await this._seek(timestamp);
    }

    this._rebuildPixiStage();

    this._render();

    this._seekingTo = null;
  }

  /**
   * Load all media into "future" to prepare for seeking to a project timestamp
   */
  _initMedia(mediaList = []) {
    this.media = {
      all: mediaList,
      past: [],
      active: [],
      future: [...mediaList].sort((l, r) => l.start - r.start),
    };
  }

  /**
   * Quickly update lists of active and inactive media to match a given timestamp
   *
   * When project is playing, updateMediaTimeline is called on every frame
   * updateMediaTimeline is accordingly optimized to quickly determine when active and inactive media
   * should or should not change for small time increments
   */
  _updateMediaTimeline(timestamp) {
    if (timestamp < this._requestedTime) {
      this._initMedia(this.media.all);
    }

    while (this.media.future[0]?.start <= timestamp) {
      const item = this.media.future.shift();
      this._insertActive(item);
    }

    while (
      this.media.active[0] &&
      !activeForTimestamp(this.media.active[0], timestamp)
    ) {
      const item = this.media.active.shift();
      this.media.past.push(item);
    }

    this._requestedTime = timestamp;
  }

  _insertActive(item) {
    let insertIndex = 0;

    while (
      this.media.active.length > insertIndex &&
      endsAfterItem(item, this.media.active[insertIndex])
    ) {
      insertIndex += 1;
    }
    this.media.active.splice(insertIndex, 0, item);
  }

  async _seek(timestamp) {
    await Promise.all(
      this.media.active.map((item) => item.seek(timestamp - item.start))
    );
    this.greensockTimeline.seek(timestamp);
    this.stopwatch.synchronize(timestamp);
  }

  _rebuildPixiStage() {
    if (this._pixiStage) {
      this._pixiStage.removeChildren();
      const displayObjects = this.media.active
        .map((item) => item.displayObject)
        .filter((displayObject) => displayObject && !displayObject._destroyed);
      const alignmentGuides = AlignmentGuides.getInstance(this.app);
      if (displayObjects.length) {
        try {
          this._pixiStage.addChild(
            ...displayObjects,
            alignmentGuides.container
          );
        } catch (error) {
          logger.error('Unable to add display objects to stage', { error });
        }
      }
    }
  }

  _render() {
    const inactive = [...this.media.past, ...this.media.future];
    inactive.forEach((item) => item.deactivate());
    this.media.active.forEach((item) => item.render(this.stopwatch.playing));

    this.renderedTime = this._requestedTime;
  }

  /**
   * Build the master GreenSock Animation Package timeline from the timelines of the media items
   */
  _buildGreensockTimeline() {
    this.greensockTimeline.pause();
    this.greensockTimeline.clear();

    // build the overall timeline
    this.media.all.forEach((item) => {
      if (item.greensockTimeline) {
        item.greensockTimeline.seek(0);
        // greensock doesn't play nice with negative positions, so we safeguard against them here
        this.greensockTimeline.add(
          item.greensockTimeline,
          Math.max(item.start, 0)
        );
      }
    });

    this.greensockTimeline.seek(this.stopwatch.currentTime, false);
    if (this.stopwatch.playing) {
      this.greensockTimeline.play();
    }
  }
}
